import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { getApiBaseUrl } from '../../config/apiBase';
import { 
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const SMSTrace = () => {
  const API_BASE_URL = getApiBaseUrl();
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('auto'); // Default to auto-detect
  const [loading, setLoading] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  const handleTrace = async () => {
    if (!query.trim()) {
      setError('Please enter a value to trace');
      return;
    }

    setLoading(true);
    setError(null);
    setTraceData(null);

    try {
      const token = localStorage.getItem('token');
      
      // Build query parameter based on search type
      const searchParam = searchType === 'auto' ? query : `${searchType}:${query}`;
      const response = await api.get(`/master-admin/sms-trace?query=${encodeURIComponent(searchParam)}`);
      
      setTraceData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trace SMS');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusIcon = (status) => {
    if (status?.includes('FAILED') || status?.includes('ERROR')) {
      return <XCircleIcon className="w-4 h-4 text-red-500" />;
    }
    if (status === 'DELIVERED' || status === 'PASSED' || status?.includes('SUBMITTED')) {
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
    if (status?.includes('PENDING') || status?.includes('RETRY')) {
      return <ClockIcon className="w-4 h-4 text-yellow-500" />;
    }
    return <ClockIcon className="w-4 h-4 text-gray-500" />;
  };

  const getStatusColor = (status) => {
    if (status?.includes('FAILED') || status?.includes('ERROR')) {
      return 'bg-red-100 text-red-800';
    }
    if (status === 'DELIVERED' || status === 'PASSED' || status?.includes('SUBMITTED')) {
      return 'bg-green-100 text-green-800';
    }
    if (status?.includes('PENDING') || status?.includes('RETRY')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const renderTimelineItem = (item, index) => {
    const isExpanded = expandedItems.has(index);
    
    return (
      <div key={index} className="relative pl-8 pb-8">
        {/* Timeline line */}
        {index < (traceData?.timeline?.length || 0) - 1 && (
          <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200" />
        )}
        
        {/* Timeline dot */}
        <div className="absolute left-2 top-2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full" />
        
        {/* Content */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{item.stage}</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
              {getStatusIcon(item.status)}
              <span className="ml-1">{item.status}</span>
            </span>
            {item.time && (
              <span className="text-sm text-gray-500">
                {formatTimestamp(item.time)}
              </span>
            )}
          </div>
          
          {item.error_msg && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{item.error_msg}</p>
            </div>
          )}
          
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Details</span>
                <button
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  onClick={() => toggleExpanded(index)}
                >
                  {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                </button>
              </div>
              
              {isExpanded && (
                <div className="space-y-2 mt-2">
                  {Object.entries(item.data || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600 capitalize">
                        {key.replace(/_/g, ' ')}:
                      </span>
                      <div className="flex items-center gap-2 max-w-md">
                        <span className="text-sm text-right break-all">
                          {typeof value === 'object' 
                            ? JSON.stringify(value, null, 2)
                            : String(value)
                          }
                        </span>
                        <button
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          onClick={() => copyToClipboard(String(value))}
                        >
                          <DocumentDuplicateIcon className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">SMS Trace & Timeline Debug</h1>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          Master Admin Only
        </span>
      </div>

      {/* Search Input */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Trace SMS</h2>
        </div>
        <div className="p-6">
          <div className="flex gap-2 mb-3">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="auto">Auto-detect</option>
              <option value="message_id">Message ID</option>
              <option value="mobile_number">Mobile Number</option>
              <option value="campaign_id">Campaign ID</option>
              <option value="reference_id">Reference ID</option>
            </select>
            <input
              type="text"
              placeholder={searchType === 'auto' 
                ? "Enter value (auto-detect type)" 
                : `Enter ${searchType.replace('_', ' ')}`
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTrace()}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleTrace}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
              )}
              Trace SMS
            </button>
          </div>
          {searchType === 'auto' && (
            <p className="text-xs text-gray-500 mb-2">
              💡 Tip: System will automatically detect the input type. You can also manually select the type for more precise results.
            </p>
          )}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {traceData && (
        <>
          {/* Summary Badges */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                SMS Summary
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Input Type: {traceData.input_type}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Trace ID: {traceData.trace_id}
                </span>
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="text-center">
                  <CheckCircleIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.ingested ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className="text-sm">Ingested</span>
                </div>
                <div className="text-center">
                  <CheckCircleIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.validated ? 'text-green-500' : 'text-red-500'}`} />
                  <span className="text-sm">Validated</span>
                </div>
                <div className="text-center">
                  <CheckCircleIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.routed ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className="text-sm">Routed</span>
                </div>
                <div className="text-center">
                  <CheckCircleIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.submitted ? 'text-green-500' : 'text-red-500'}`} />
                  <span className="text-sm">Submitted</span>
                </div>
                <div className="text-center">
                  <ArrowPathIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.retry_count > 0 ? 'text-yellow-500' : 'text-gray-300'}`} />
                  <span className="text-sm">Retries ({traceData.summary.retry_count})</span>
                </div>
                <div className="text-center">
                  <CheckCircleIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.delivered ? 'text-green-500' : 'text-red-500'}`} />
                  <span className="text-sm">Delivered</span>
                </div>
                <div className="text-center">
                  <CurrencyDollarIcon className={`w-6 h-6 mx-auto mb-1 ${traceData.summary.billed ? (traceData.summary.refunded ? 'text-yellow-500' : 'text-green-500') : 'text-gray-300'}`} />
                  <span className="text-sm">
                    {traceData.summary.refunded ? 'Refunded' : (traceData.summary.billed ? 'Billed' : 'Not Billed')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">SMS Lifecycle Timeline</h2>
            </div>
            <div className="p-6">
              {traceData.timeline && traceData.timeline.length > 0 ? (
                <div className="space-y-4">
                  {traceData.timeline.map((item, index) => renderTimelineItem(item, index))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No timeline data available for this SMS
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SMSTrace;
